import {
  createContext,
  useContext,
  useEffect,
  useId,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { MOTION } from '../theme/tokens.js';
import { Button } from './Button.js';
import { Dialog } from './Dialog.js';
import { errorCopyFor } from './errorCopy.js';
import { SUPPORT_CODE_LEAD } from './diagnostic.js';
import {
  createDialogStore,
  DISMISSED,
  type AlertEntry,
  type ConfirmEntry,
  type DialogApi,
  type DialogEntry,
  type DialogResult,
  type DialogStore,
  type PromptEntry,
} from './dialog-store.js';

/**
 * `useDialog()`: promise-based alert, confirm, and prompt.
 *
 * This is where the ESLint ban on `alert`, `confirm`, and `prompt` sends
 * people, so it has to be at least as pleasant as the thing it replaces:
 *
 *   if (await confirm({ title: 'Void this play?', tone: 'danger' })) { ... }
 *
 * Mount one `<DialogProvider>` at the app root. Everything below it can ask a
 * question without threading open state through its parents, which is the
 * actual reason call sites reach for `window.confirm` in the first place.
 */

const DialogStoreContext = createContext<DialogStore | null>(null);

export interface DialogProviderProps {
  readonly children?: ReactNode | undefined;
  /** Supply a store to drive the queue from outside React, as the tests do. */
  readonly store?: DialogStore | undefined;
}

export function DialogProvider({ children, store }: DialogProviderProps): ReactNode {
  const [resolved] = useState(() => store ?? createDialogStore());

  // Only a store this provider CREATED gets torn down here. A store passed in
  // belongs to the caller, outlives this component by design, and settling its
  // queue on unmount would be this component reaching into something it does
  // not own.
  const owned = store === undefined;
  useEffect(
    () => () => {
      if (owned) resolved.settleAll({ confirmed: false, value: '' });
    },
    [owned, resolved],
  );

  return (
    <DialogStoreContext.Provider value={resolved}>
      {children}
      <DialogHost store={resolved} />
    </DialogStoreContext.Provider>
  );
}

export function useDialog(): DialogApi {
  const store = useContext(DialogStoreContext);
  if (store === null) {
    throw new Error('useDialog() needs a <DialogProvider> above it. Mount one at the app root.');
  }
  return store;
}

function noEntry(): DialogEntry | null {
  return null;
}

function DialogHost({ store }: { readonly store: DialogStore }): ReactNode {
  const entry = useSyncExternalStore(store.subscribe, store.getCurrent, noEntry);
  const [rendered, setRendered] = useState<DialogEntry | null>(entry);

  useEffect(() => {
    if (entry !== null) {
      setRendered(entry);
      return undefined;
    }
    /*
     * Nothing rendered means nothing to animate out, so schedule nothing.
     *
     * Without this the effect set a timer on EVERY mount, including the
     * overwhelmingly common one where no dialog has ever opened. That timer is
     * harmless while the component lives and is cleaned up on unmount, but a
     * root that is never unmounted keeps it pending past the end of a test
     * file, and it then fires against a torn-down jsdom as
     * "ReferenceError: window is not defined".
     *
     * That is what has been failing `npm run gates` intermittently on branches
     * touching neither this file nor dialogs. A reviewer measured it at about
     * one full run in five; do not trust a higher number, including the "half
     * the time" I first wrote here from casual observation.
     *
     * `wiring.test.tsx` imports the real entry point, which calls
     * `createRoot().render()` and never unmounts it, so the provider mounted
     * there outlives every test in the file. A fresh-context pass on
     * `fix/sw-update-prompt` did observe that leaked root, and I first wrote
     * here that "an earlier review recorded it as an S3", which is FALSE: the
     * observation never reached `docs/reviews/fix-sw-update-prompt.md`, whose
     * only "never unmounts" line is about the `UpdatePrompt` component in the
     * running app. A reviewer went looking for my citation and could not find
     * it. That is the shape of comment this project keeps paying for and I
     * wrote one into a fix for the same class of problem.
     *
     * Why the timer is what the jsdom teardown cannot clear: vitest's
     * `populateGlobal` leaves `globalThis.setTimeout` as NODE's, not jsdom's,
     * so `dom.window.close()` and its `stopAllTimers()` never see it.
     *
     * Fixed here rather than in the test because the timer is pointless in this
     * state regardless: there is no exit animation for a dialog that was never
     * on screen. That does NOT close the class. The leaked root is untouched,
     * and this still schedules a timer whenever a dialog has actually been
     * answered, so any file that mounts twice without unmounting, or that grows
     * a `Toast` inside that root, brings it back.
     */
    if (rendered === null) return undefined;

    // The promise has already resolved. Keep the answered dialog mounted and
    // closed for one motion beat so it animates out instead of vanishing.
    const timer = setTimeout(() => {
      setRendered(null);
    }, MOTION.sheet);
    return () => {
      clearTimeout(timer);
    };
  }, [entry, rendered]);

  if (rendered === null) return null;

  const open = entry !== null;
  const settle = (result: DialogResult): void => {
    store.settle(rendered.id, result);
  };

  switch (rendered.kind) {
    case 'alert':
      return <AlertDialog key={rendered.id} entry={rendered} open={open} onSettle={settle} />;
    case 'confirm':
      return <ConfirmDialog key={rendered.id} entry={rendered} open={open} onSettle={settle} />;
    case 'prompt':
      return <PromptDialog key={rendered.id} entry={rendered} open={open} onSettle={settle} />;
  }
}

interface HostedProps<TEntry extends DialogEntry> {
  readonly entry: TEntry;
  readonly open: boolean;
  readonly onSettle: (result: DialogResult) => void;
}

const CONFIRMED: DialogResult = { confirmed: true, value: null };

/** Radix reports every user-driven close through this. All of them dismiss. */
function dismissOnClose(onSettle: (result: DialogResult) => void) {
  return (next: boolean): void => {
    if (!next) onSettle(DISMISSED);
  };
}

function AlertDialog({ entry, open, onSettle }: HostedProps<AlertEntry>): ReactNode {
  const { title, body, code, diagnostic, confirmLabel = 'OK' } = entry.options;
  // Explicit copy wins; a code falls back to the themed copy for that code.
  const fallback = errorCopyFor(code);
  const heading = title ?? fallback.title;
  const description = body ?? (code === undefined ? undefined : fallback.body);

  return (
    <Dialog
      open={open}
      onOpenChange={dismissOnClose(onSettle)}
      title={heading}
      description={description}
      size="sm"
      footer={
        <Button
          onClick={() => {
            onSettle(CONFIRMED);
          }}
        >
          {confirmLabel}
        </Button>
      }
    >
      {diagnostic === undefined ? undefined : <DiagnosticLine code={diagnostic} />}
    </Dialog>
  );
}

function ConfirmDialog({ entry, open, onSettle }: HostedProps<ConfirmEntry>): ReactNode {
  const {
    title,
    body,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    tone = 'default',
  } = entry.options;

  return (
    <Dialog
      open={open}
      onOpenChange={dismissOnClose(onSettle)}
      title={title}
      description={body}
      size="sm"
      footer={
        <>
          <Button
            tone="ghost"
            onClick={() => {
              onSettle(DISMISSED);
            }}
          >
            {cancelLabel}
          </Button>
          <Button
            tone={tone === 'danger' ? 'danger' : 'primary'}
            onClick={() => {
              onSettle(CONFIRMED);
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}

function PromptDialog({ entry, open, onSettle }: HostedProps<PromptEntry>): ReactNode {
  const {
    title,
    body,
    label,
    placeholder,
    defaultValue = '',
    numeric = false,
    confirmLabel = 'Save',
    cancelLabel = 'Cancel',
    mustMatch,
    tone = 'default',
  } = entry.options;
  const [value, setValue] = useState(defaultValue);
  const fieldId = useId();
  const blocked = mustMatch !== undefined && value.trim() !== mustMatch.trim();

  const submit = (): void => {
    if (blocked) return;
    onSettle({ confirmed: true, value });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={dismissOnClose(onSettle)}
      title={title}
      description={body}
      size="sm"
      footer={
        <>
          <Button
            tone="ghost"
            onClick={() => {
              onSettle(DISMISSED);
            }}
          >
            {cancelLabel}
          </Button>
          <Button
            tone={tone === 'danger' ? 'danger' : 'primary'}
            disabled={blocked}
            onClick={submit}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="gs-field">
        <label className="gs-field__label" htmlFor={fieldId}>
          {label}
        </label>
        <input
          id={fieldId}
          className="gs-input"
          value={value}
          placeholder={placeholder}
          inputMode={numeric ? 'numeric' : 'text'}
          autoFocus
          onChange={(event) => {
            setValue(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
        />
      </div>
    </Dialog>
  );
}

/**
 * The diagnostic code, and nothing else from the failure.
 *
 * Shown as data (mono, tabular) rather than as prose, because its only job is
 * to be read aloud or typed into a message accurately.
 */
export function DiagnosticLine({ code }: { readonly code: string }): ReactNode {
  return (
    <p className="gs-diagnostic">
      <span>{SUPPORT_CODE_LEAD}</span> <span className="gs-diagnostic__code">{code}</span>
    </p>
  );
}
